import React from 'react';
import { Drawer, Form, Input, Select, Button, Typography, Space, InputNumber, notification } from 'antd';
import { UserOutlined, BankOutlined, TeamOutlined, PhoneOutlined, MailOutlined, SendOutlined } from '@ant-design/icons';
import { atom, useAtom } from 'jotai';
import { permColors } from '../../utils/colors';
import { countries } from '../../utils/arrayData/CountryArray';
import axios from 'axios';
import { mainUrl } from '../../api/Urls';

const { Title, Text } = Typography;
const { Option } = Select;

export const hrContactDrawerAtom = atom(false);

export const ButtonHrContactDrawer: React.FC<{
  text?: string;
  className?: string;
  type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
}> = ({ text = 'Get Custom Quote', className = '', type = 'primary' }) => {
  const [isOpen, setIsOpen] = useAtom(hrContactDrawerAtom);
  const toggleDrawer = () => setIsOpen(!isOpen);

  return (
    <Button onClick={toggleDrawer} className={className} type={type}>
      {text}
    </Button>
  );
};

interface FormData {
  name: string;
  company_name: string;
  email: string;
  phone_number: string;
  country: string;
  staff_count: number;
  service_type: 'eor' | 'contractor' | 'both';
  message?: string;
}

const HrContactPopup: React.FC = () => {
  const [isOpen, setIsOpen] = useAtom(hrContactDrawerAtom);
  const [form] = Form.useForm<FormData>();
  const [loading, setLoading] = React.useState(false);

  const closeDrawer = () => setIsOpen(false);

  const handleSubmit = async (values: FormData) => {
    setLoading(true);
    try {
        await axios.post(mainUrl + '/eor/enquiry/', values);
        form.resetFields();
        closeDrawer();
        notification.success({
            message: 'Thank you!',
            description: 'We\'ll get back to you within 24 hours.',
        });
       
    } catch (error) {
      console.error('Error submitting form:', error);
      notification.error({
        message: 'Error',
        description: 'Something went wrong. Please try again.',
      });
      // Handle error - show error notification
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    closeDrawer();
  };

  return (
    <Drawer
      title={null}
      placement="right"
      width={640}
      onClose={handleClose}
      open={isOpen}
      styles={{
        body: { padding: 0 },
        header: { display: 'none' }
      }}
      closable={false}
      maskClosable={false}
      maskStyle={{
        backdropFilter: 'blur(4px)',
        backgroundColor: 'rgba(0, 0, 0, 0.3)'
      }}
      destroyOnClose
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <div 
          className="px-6 py-8 text-white bg-antBlue"
        
        >
            <div className="max-w-[700px] ">
          <Title level={3} className="text-white mb-2">
            Get Started Today
          </Title>
          <Text className="text-white/90">
            Tell us about your hiring needs and we'll create a custom solution for you.
          </Text>
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 px-6 py-6 bg-white">
          <Form
            form={form}
            layout="vertical"
            
            onFinish={handleSubmit}
            requiredMark={false}
            className="h-full flex flex-col max-w-[700px] "
          >
            <div className="flex-1 space-y-3">
                <div className="flex flex-col md:flex-row gap-3">
              <Form.Item
                name="name"
                label="Full Name"
                className="w-full mb-0"
                rules={[
                  { required: true, message: 'Please enter your full name' },
                  { min: 2, message: 'Name must be at least 2 characters' }
                ]}
              >
                <Input
                  prefix={<UserOutlined className="text-gray-400" />}
                  placeholder="Enter your full name"
                  size="large"
                  className="rounded-lg mb-0"
                />
              </Form.Item>
              <Form.Item
                name="company_name"
                label="Company Name"
                className="w-full mb-0"
                rules={[
                  { required: true, message: 'Please enter your company name' },
                  { min: 2, message: 'Company name must be at least 2 characters' }
                ]}
              >
                <Input
                  prefix={<BankOutlined className="text-gray-400" />}
                  placeholder="Enter your company name"
                  size="large"
                  className="rounded-lg mb-0"
                />
              </Form.Item>
              </div>
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input
                  prefix={<MailOutlined className="text-gray-400" />}
                  placeholder="Enter your email address"
                  size="large"
                  className="rounded-lg"
                />
              </Form.Item>

              <div className="flex flex-col md:flex-row gap-3">
                <Form.Item
                  name="country"
                  label="Country"
                  className="w-full md:w-1/3 mb-0"
                  rules={[
                    { required: true, message: 'Please select your country' }
                  ]}
                >
                  <Select
                    placeholder="Select country"
                    size="large"
                    className="rounded-lg"
                    showSearch
                    filterOption={(input, option) =>
                      (option?.children as any)?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {countries
                      .filter(country => country !== '----')
                      .map((country) => (
                        <Option key={country} value={country}>
                          {country}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="phone_number"
                  label="Phone Number"
                  className="w-full md:w-2/3 mb-0"
                  rules={[
                    { required: true, message: 'Please enter your phone number' },
                  ]}
                >
                  <Input
                    prefix={<PhoneOutlined className="text-gray-400" />}
                    placeholder="Enter your phone number"
                    size="large"
                    className="rounded-lg"
                    type="number"
                  />
                </Form.Item>
              </div>

              <Form.Item
                name="staff_count"
                label="How many people do you plan to hire?"
                rules={[
                  { required: true, message: 'Please enter the number of staff' },
                  { type: 'number', min: 1, message: 'Must be at least 1' }
                ]}
              >
                <InputNumber
                  prefix={<TeamOutlined className="text-gray-400" />}
                  placeholder="Number of staff"
                  size="large"
                  className="w-full rounded-lg"
                  min={1}
                  max={1000}
                />
              </Form.Item>

              <Form.Item
                name="service_type"
                label="What type of service do you need?"
                rules={[
                  { required: true, message: 'Please select a service type' }
                ]}
              >
                <Select
                  placeholder="Select service type"
                  size="large"
                  className="rounded-lg"
                  optionLabelProp="label"
                >
                  <Option value="eor" label="Employer of Record (EOR)">
                    <div className="py-1">
                      <div className="font-medium">Employer of Record (EOR)</div>
                      <div className="text-xs text-gray-500">For hiring full-time employees</div>
                    </div>
                  </Option>
                  <Option value="contractor" label="Contractor Management">
                    <div className="py-1">
                      <div className="font-medium">Contractor Management</div>
                      <div className="text-xs text-gray-500">For managing independent contractors</div>
                    </div>
                  </Option>
                  <Option value="both" label="Both Services">
                    <div className="py-1">
                      <div className="font-medium">Both Services</div>
                      <div className="text-xs text-gray-500">I need both employees and contractors</div>
                    </div>
                  </Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="message"
                label="Additional Information (Optional)"
              >
                <Input.TextArea
                  placeholder="Tell us more about your specific needs..."
                  rows={3}
                  className="rounded-lg"
                />
              </Form.Item>
            </div>

            {/* Footer with buttons */}
            <div className="pt-6 border-t border-gray-100">
              <Space className="w-full" direction="vertical" size="middle">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={loading}
                  icon={<SendOutlined />}
                  className="w-full h-12 rounded-lg font-semibold text-base bg-antBlue hover:bg-antBlue/80"
                >
                  {loading ? 'Submitting...' : 'Submit'}
                </Button>
                
                <Button
                  type="text"
                  size="large"
                  onClick={handleClose}
                  className="w-full h-10 text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </Button>
              </Space>
              
              <div className="text-center mt-4">
                <Text className="text-xs text-gray-400">
                  We'll get back to you within 24 hours
                </Text>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </Drawer>
  );
};

export default HrContactPopup;
